import PropTypes from 'prop-types'
import React, { useEffect, useRef } from 'react'
import EditorialArtifacts from 'components/editorial/editorial-artifacts'
import ImageCarouselAutoPlayVideo from 'components/pdp/carousel-images/image-carousel/image-carousel-auto-play-video/ImageCarouselAutoPlayVideo'
import StateStorage from 'lib/state-storage'
import useIntersection from 'components/custom-hooks/useIntersection'
import { SEGMENT_REF_TYPES } from 'lib/segment/const'
import {
  segmentBannerViewEvent,
  segmentBannerClickEvent,
  segmentBannerTaggedProductsClicked,
} from 'components/home/home-page-segment/homePageSegment'
import Router from 'next/router'
import { prepareCategoryReferer } from '../../utils'
import getHomepageCtaInfo from '../../cta-layout'
import DesktopSlideImages from '../../desktop-slide-images'
import SlideContentProducts from './slide-content-products/SlideContentProducts'

const SlideContentMedia = ({
  isPhone,
  idx,
  banner,
  isAutoPlayVideo,
  isLoopVideo,
  onHandleNext,
}) => {
  const shop = StateStorage.getShop()
  const language = StateStorage.getLanguage()
  const feedId = banner?.id
  const media = banner?.attributes?.media
  const isVideo = media?.kind === 'video'
  const slideContentMediaRef = useRef(null)
  const isIntersecting = useIntersection(slideContentMediaRef, {
    once: false,
  })
  const cta = banner?.attributes?.cta
  const isCategoryCta = cta?.kind === 'category'
  const linkAs = isCategoryCta
    ? `/${shop}/${language}/clothes/${cta?.link_rewrite}`
    : `${cta?.value}`
  const linkHref = isCategoryCta
    ? `/category?id=${cta?.value}`
    : `${cta?.value}`

  const handleClickSlide = () => {
    Router.push(linkHref, linkAs)
    segmentBannerClickEvent({
      ref_type: SEGMENT_REF_TYPES.APP_FEED,
      ref_id: feedId,
      cta_type: cta?.kind,
    })
  }

  useEffect(() => {
    if (isIntersecting) {
      segmentBannerViewEvent({
        ref_type: SEGMENT_REF_TYPES.APP_FEED,
        ref_id: feedId,
      })
    }
  }, [isIntersecting, feedId])

  if (isVideo) {
    const autoPlay = isAutoPlayVideo
    const title = banner?.attributes?.title
    const title_info = banner?.attributes?.title_info
    const description = banner?.attributes?.subtitle
    const description_info = banner?.attributes?.subtitle_info
    const refId = banner?.id

    const artifactsProps = {
      ctas: [
        {
          cta: cta?.title,
          cta_box_color: cta?.info?.[`button-color`],
          cta_caption_color: cta?.info?.[`text-color`],
          cta_info: getHomepageCtaInfo(
            cta?.info,
            Boolean(banner?.relationships?.products?.data?.length),
          ),
          cta_link: linkAs,
          cta_href: linkHref,
          id_cta: 1755,
          is_category_page: isCategoryCta,
          is_landing_page: !isCategoryCta,
          is_link_static: 1,
        },
      ],
      title,
      title_info,
      description,
      description_info,
    }
    return (
      <div
        role="button"
        tabIndex={0}
        ref={slideContentMediaRef}
        className="swiper-slide relative editorial-video-slide-container"
        key={media.url}
        onClick={() => {
          handleClickSlide()
        }}
        onKeyDown={() => {
          handleClickSlide()
        }}
      >
        <SlideContentProducts
          feedId={feedId}
          banner={banner}
          shopLookCtaEventTracking={() =>
            segmentBannerTaggedProductsClicked({
              ref_type: SEGMENT_REF_TYPES.APP_FEED,
              ref_id: feedId,
            })
          }
        />
        <ImageCarouselAutoPlayVideo
          loadWhenActive
          desktopHeroPoster={!isPhone}
          loop={isLoopVideo}
          autoPlay={autoPlay}
          poster={media.thumbnail_url}
          src={media.url}
          className="editorial-video-slide-container__video"
          onVideoEnd={() => onHandleNext(idx)}
        />
        <div className="editorial-artifacts">
          <EditorialArtifacts
            {...artifactsProps}
            ctas={isPhone ? [] : artifactsProps.ctas}
            linkToCategoryCaptured={() => {
              prepareCategoryReferer(idx + 1, refId)
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      key={media.url}
      ref={slideContentMediaRef}
      onClick={() => {
        handleClickSlide()
      }}
      onKeyDown={() => {
        handleClickSlide()
      }}
      className="swiper-slide relative editorial-image-slide-container"
    >
      <SlideContentProducts
        feedId={feedId}
        banner={banner}
        shopLookCtaEventTracking={() =>
          segmentBannerTaggedProductsClicked({
            ref_type: SEGMENT_REF_TYPES.APP_FEED,
            ref_id: feedId,
          })
        }
      />
      <DesktopSlideImages
        isActive={isAutoPlayVideo}
        key={idx}
        idx={idx}
        slide={banner}
        isPhone={isPhone}
      />
    </div>
  )
}

SlideContentMedia.propTypes = {
  idx: PropTypes.number.isRequired,
  isAutoPlayVideo: PropTypes.bool.isRequired,
  isLoopVideo: PropTypes.bool.isRequired,
  banner: PropTypes.shape({
    url: PropTypes.string,
    image: PropTypes.string,
    full_image: PropTypes.string,
    full_image_mobile: PropTypes.string,
    relationships: PropTypes.shape({
      products: PropTypes.shape({
        data: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    }),
    attributes: PropTypes.shape({
      cta: PropTypes.shape({
        kind: PropTypes.string,
        title: PropTypes.string,
        value: PropTypes.string,
      }),
      title: PropTypes.string,
      title_info: PropTypes.shape({}),
      subtitle: PropTypes.string,
      subtitle_info: PropTypes.shape({}),
      media: PropTypes.shape({ kind: PropTypes.string, url: PropTypes.string }),
    }),
    id: PropTypes.string,
  }).isRequired,
  isPhone: PropTypes.bool.isRequired,
  onHandleNext: PropTypes.func.isRequired,
}

export default SlideContentMedia
